import { Calendar, ChevronDown, ChevronLeft } from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
    Button,
    ScrollArea,
    Separator,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
    cn,
} from 'shared-ui';

import type { PoiHistoryEvent } from '@/api/poiHistoryApi';
import { StatusPill } from '@/components/shared/StatusPill';
import { PoiPanelField, PoiWhat3WordsField } from '../PoiPanelFields';
import { formatIsoDate, formatIsoDateTime } from '@/utils/dateUtils';
import { getPoiHistoryEventTitle } from './poiHistoryLabels';
import { getPoiHistoryEventPillPresentation } from './poiHistoryPillPresentation';
import { formatLeakageRateMlD } from '@/components/map/utils/poiFeatureProperties';

interface DetailRow {
    label: string;
    value: string | null;
    /**
     * Which read-only control the row mimics, matching the details-panel forms: 'choice'
     * draws the dropdown chevron, 'date' the calendar glyph, 'text' a plain input. The
     * affordances are decorative — every field here is historical and read-only.
     */
    kind?: 'text' | 'choice' | 'date';
    /** Unit prefix inside the control (the panels' Leakage Rate "Ml/D" idiom). */
    unit?: string;
}

function formatBoolean(value: boolean | null | undefined): string | null {
    if (value === null || value === undefined) return null;
    return value ? 'Yes' : 'No';
}

function formatNumber(value: number | null | undefined): string | null {
    if (value === null || value === undefined) return null;
    return String(value);
}

function formatCoordinates(latitude: number | null, longitude: number | null): string | null {
    if (latitude === null || longitude === null) return null;
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
}

/**
 * "21" for an original POI record, "21-1" for an additional leak recorded against it —
 * the stop-suffix descriptor technicians already use for Find & Fix leaks.
 */
function formatStopReference(stop: number, suffix: number): string {
    return suffix > 0 ? `${String(stop)}-${String(suffix)}` : String(stop);
}

/**
 * A reason with its free-text elaboration folded in: choosing "Other" on the technician
 * forms opens a free-text box, and that detail belongs with the reason it explains —
 * "Other – Bespoke blocker" — not floating separately among the notes.
 */
function withOtherDetail(reason: string | null, other: string | null): string | null {
    const detail = other?.trim();
    if (detail == null || detail === '') return reason;
    return reason == null || reason === '' ? detail : `${reason} – ${detail}`;
}

// Every historic record can be partial, so each row renders an em dash for a missing
// value rather than being dropped: the field labels tell the user what COULD have been
// recorded. Only whole sections with nothing to show for the event type are hidden.

function basicDetailRows(event: PoiHistoryEvent): DetailRow[] {
    if (event.assessmentDetails) {
        const d = event.assessmentDetails;
        return [
            { label: 'Outcome', value: event.summary, kind: 'choice' },
            { label: 'Stop', value: formatStopReference(d.stop, d.suffix) },
            { label: 'Date of Search', value: formatIsoDate(d.searchDate), kind: 'date' },
            { label: 'Date Created', value: formatIsoDate(d.surveyedDate), kind: 'date' },
            { label: 'Pipe Type', value: d.pipeType, kind: 'choice' },
            {
                label: 'Leakage Rate',
                value: d.leakageRate == null ? formatNumber(d.leakageRate) : formatLeakageRateMlD(d.leakageRate),
                kind: 'choice',
                unit: 'Ml/D',
            },
            { label: 'Leak Visible?', value: formatBoolean(d.isLeakVisible), kind: 'choice' },
            { label: 'Repaired?', value: formatBoolean(d.repaired), kind: 'choice' },
            { label: 'No Dig® Repair?', value: formatBoolean(d.nodigFix), kind: 'choice' },
            { label: 'No Dig® Suitable', value: d.nodigSuitable, kind: 'choice' },
            { label: 'Smart Meter Found?', value: formatBoolean(d.smartMeterFound), kind: 'choice' },
            { label: 'Work Order Number', value: d.clientWorkOrderNumber },
            { label: 'Reason', value: withOtherDetail(d.leakResultReason, d.leakResultReasonOther), kind: 'choice' },
        ];
    }
    if (event.noDigLeakDetails) {
        const d = event.noDigLeakDetails;
        return [
            { label: 'Status', value: d.status, kind: 'choice' },
            { label: 'Source', value: d.sourceType, kind: 'choice' },
            { label: 'Registered', value: formatIsoDateTime(d.created), kind: 'date' },
            { label: 'Pipe Type', value: d.pipeType, kind: 'choice' },
            {
                label: 'Leakage Rate',
                value: d.leakageRate == null ? formatNumber(d.leakageRate) : formatLeakageRateMlD(d.leakageRate),
                kind: 'choice',
                unit: 'Ml/D',
            },
            { label: 'Leak Visible?', value: formatBoolean(d.isLeakVisible), kind: 'choice' },
            { label: 'Smart Meter Found?', value: formatBoolean(d.smartMeterFound), kind: 'choice' },
            { label: 'Already Marked Up?', value: formatBoolean(d.alreadyMarkedUp), kind: 'choice' },
        ];
    }
    if (event.repairDetails) {
        const d = event.repairDetails;
        return [
            { label: 'Outcome', value: event.summary, kind: 'choice' },
            { label: 'Application Number', value: formatNumber(d.applicationNumber) },
            { label: 'Site Visited', value: formatIsoDate(d.siteVisited), kind: 'date' },
            { label: 'Repaired Date', value: formatIsoDate(d.repairedDate), kind: 'date' },
            { label: 'Mains Pressure', value: formatNumber(d.mainsPressure) },
            { label: 'Pressure Band', value: d.pressureBand, kind: 'choice' },
            { label: 'Batch', value: d.batchName },
            { label: 'Pipe Side', value: d.pipeSide, kind: 'choice' },
            {
                label: 'Unsuitable Reason',
                value: withOtherDetail(d.unsuitableReason, d.unsuitableReasonOther),
                kind: 'choice',
            },
            { label: 'Work Order Number', value: d.clientWorkOrderNumber },
        ];
    }
    if (event.recheckDetails) {
        const d = event.recheckDetails;
        return [
            { label: 'Result', value: d.result, kind: 'choice' },
            { label: 'Recheck Date', value: formatIsoDate(d.recheckDate), kind: 'date' },
            { label: 'Mains Pressure', value: formatNumber(d.mainsPressure) },
            { label: 'Work Order Number', value: d.clientWorkOrderNumber },
        ];
    }
    return [];
}

interface LocationInfo {
    whatThreeWords: string | null;
    address: string | null;
    coordinates: string | null;
}

/**
 * Location facts per event type. Repairs and rechecks record no location of their own
 * (they happen at their leak's location), so the whole section is hidden for them.
 */
function locationInfo(event: PoiHistoryEvent): LocationInfo | null {
    if (event.assessmentDetails) {
        const d = event.assessmentDetails;
        return {
            whatThreeWords: d.leakWhatThreeWords,
            address: d.leakageAddress,
            coordinates: formatCoordinates(d.latitude, d.longitude),
        };
    }
    if (event.noDigLeakDetails) {
        const d = event.noDigLeakDetails;
        return {
            whatThreeWords: d.whatThreeWords,
            address: d.address,
            coordinates: formatCoordinates(d.latitude, d.longitude),
        };
    }
    return null;
}

/**
 * The technician's free-text comments for the event, written through the technician
 * app's Comments field on the assessment/repair/recheck forms. "Other" reason detail is
 * NOT included — it renders folded into its reason row (see withOtherDetail). No Dig
 * leak registrations record no comments.
 */
function eventComments(event: PoiHistoryEvent): string | null {
    const comments =
        event.assessmentDetails?.comments ?? event.repairDetails?.comments ?? event.recheckDetails?.comments ?? null;
    return comments != null && comments.trim() !== '' ? comments : null;
}

// The details-panel form idiom (PoiPanelField's bordered read-only control), so the
// history detail reads like every other form: dates carry the calendar glyph, recorded
// choices the dropdown chevron, free values a plain input. An em dash keeps a missing
// value visible — historic records can be partial, and the label says what COULD have
// been recorded.
function DetailFieldItem({ row }: { row: DetailRow }) {
    return (
        <PoiPanelField label={row.label}>
            {row.unit != null && (
                <>
                    <span className="poi-panel-control__unit">{row.unit}</span>
                    <Separator orientation="vertical" aria-hidden="true" />
                </>
            )}
            <span className={cn('flex-1 text-sm', row.value == null && 'text-muted-foreground')}>
                {row.value ?? '—'}
            </span>
            {row.kind === 'date' && <Calendar className="poi-panel-control__trailing size-4" aria-hidden="true" />}
            {row.kind === 'choice' && <ChevronDown className="poi-panel-control__trailing size-4" aria-hidden="true" />}
        </PoiPanelField>
    );
}

// Section titles: Poppins Bold 16px/24px; the expand/collapse chevron switches from the
// shared muted colour to foreground (lucide's default stroke width is already 2).
// Hover feedback is Origin Blue alone — matching the expanded state's colour — with the
// base component's hover underline switched off per the design.
const ACCORDION_TRIGGER_CLASSES =
    'text-base leading-6 font-bold tracking-normal hover:text-origin-blue hover:no-underline **:data-[slot=accordion-trigger-icon]:text-foreground';

// Fields stack 16px apart, as on the details tab. The shared accordion body is styled
// for prose and gives every non-final <p> a 16px bottom margin; a field's label is such a
// <p>, so without the override each label would sit 20px above its control instead of
// the details tab's 4px. Same variant selector so tailwind-merge replaces, not stacks.
const ACCORDION_CONTENT_CLASSES = 'space-y-4 [&_p:not(:last-child)]:mb-0';

interface PoiHistoryEventDetailProps {
    event: PoiHistoryEvent;
    onBack: () => void;
}

/**
 * Full-panel detail view for one history event (PBI 278). Replaces the tabs area of the
 * POI panel (the panel heading and close button remain above); the back button returns
 * to the History tab. Sections: Basic Details, Location and Notes — hidden entirely when
 * the event type has nothing to show for them. Signatures are deliberately absent (a
 * later phase adds them once attachments are served to the Orbit portal).
 */
export function PoiHistoryEventDetail({ event, onBack }: PoiHistoryEventDetailProps) {
    const title = getPoiHistoryEventTitle(event);
    const pill = getPoiHistoryEventPillPresentation(event);
    const rows = basicDetailRows(event);
    const location = locationInfo(event);
    const comments = eventComments(event);

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex flex-wrap items-center gap-2 border-b px-4 py-4">
                {/* Icon-only back affordance with a "Back" tooltip; the aria-label keeps the
                    full destination for screen readers. text-primary is set explicitly on the
                    icon because TooltipTrigger overwrites the button's data-slot, which would
                    otherwise drop the shared ghost-button colour rule. */}
                <Tooltip>
                    <TooltipTrigger
                        render={
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="panel-close-btn size-10 shrink-0 cursor-pointer"
                                onClick={onBack}
                                aria-label="Back to History"
                            >
                                <ChevronLeft className="text-primary size-5" aria-hidden="true" />
                            </Button>
                        }
                    />
                    <TooltipContent side="top">Back</TooltipContent>
                </Tooltip>
                <h3 className="text-foreground text-2xl leading-8 font-black tracking-normal uppercase">{title}</h3>
                {pill && <StatusPill presentation={pill} />}
            </div>
            <ScrollArea className="min-h-0 flex-1">
                {/* Sections are collapsed by default (no defaultValue) and sit flush — the
                    shared-ui divider between items is switched off per the design. */}
                <Accordion className="px-6 py-2">
                    <AccordionItem value="basic-details" className="not-last:border-b-0">
                        <AccordionTrigger className={ACCORDION_TRIGGER_CLASSES}>Basic Details</AccordionTrigger>
                        <AccordionContent className={ACCORDION_CONTENT_CLASSES}>
                            {rows.map((row) => (
                                <DetailFieldItem key={row.label} row={row} />
                            ))}
                        </AccordionContent>
                    </AccordionItem>
                    {location && (
                        <AccordionItem value="location" className="not-last:border-b-0">
                            <AccordionTrigger className={ACCORDION_TRIGGER_CLASSES}>Location</AccordionTrigger>
                            <AccordionContent className={ACCORDION_CONTENT_CLASSES}>
                                <PoiWhat3WordsField label="What3words" value={location.whatThreeWords} />
                                <DetailFieldItem row={{ label: 'Address', value: location.address }} />
                                <DetailFieldItem row={{ label: 'Latitude, Longitude', value: location.coordinates }} />
                            </AccordionContent>
                        </AccordionItem>
                    )}
                    {comments != null && (
                        <AccordionItem value="notes" className="not-last:border-b-0">
                            <AccordionTrigger className={ACCORDION_TRIGGER_CLASSES}>Notes</AccordionTrigger>
                            <AccordionContent className={ACCORDION_CONTENT_CLASSES}>
                                <PoiPanelField label="Comments" controlClassName="poi-panel-control--multiline">
                                    <span className="flex-1 text-sm whitespace-pre-wrap">{comments}</span>
                                </PoiPanelField>
                            </AccordionContent>
                        </AccordionItem>
                    )}
                </Accordion>
            </ScrollArea>
        </div>
    );
}
